import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Router from "./router";
import { bootstrap, listDashboardUsers, listProxyApplications } from "./clients/FixieApiClient";
import { BootstrapResponse, ProxyApplicationListItem } from "./models";
import { CssBaseline } from "@mui/material";
import { ProxyApplicationListState } from "./pages/ProxyApplications";
import { UserListState } from "./pages/Users";
import TagManager from 'react-gtm-module'
import { setUserId } from "./utils/trackEvent";

type AuthStatus = "AUTHENTICATED" | "UNAUTHENTICATED" | null;

const tagManagerArgs = {
  gtmId: 'GTM-5D3JRKBH'
}

TagManager.initialize(tagManagerArgs)

function useUsers(): UserListState {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date>(new Date());

  const fetchUsers = async (showToast: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await listDashboardUsers(showToast);
      if (response.ok) {
        setUsers(await response.json());
        setHasLoaded(true);
        setLastFetched(new Date());
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users,
    isLoading,
    hasLoaded,
    lastFetched,
    refetchUsers: () => fetchUsers(true),
  };
}

function useProxyApps(): ProxyApplicationListState {
  const [apps, setApps] = useState<ProxyApplicationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorString, setError] = useState<string>('');
  const [lastFetched, setLastFetched] = useState<Date>(new Date());

  const fetchProxyApplications = async (showToast: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await listProxyApplications(showToast);
      if (response.ok) {
        setApps(await response.json());
        setError('');
        setLastFetched(new Date());
        setHasLoaded(true);
      } else if (response.status !== 401) {
        throw new Error('Failed to fetch proxy applications');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchProxyApplications();
  }, []);

  return {
    apps,
    isLoading,
    hasLoaded,
    errorString,
    lastFetched,
    refetchProxyApplications: () => fetchProxyApplications(true)
  };
}

function App() {
  const [authStatus, setAuthStatus] = useState<AuthStatus>(null);
  const [bootstrapData, setBootstrapData] = useState<BootstrapResponse | null>(null);
  const proxyApplicationListState = useProxyApps();
  const userListState = useUsers();

  const fetchProfile = async () => {
    const response = await bootstrap();
    if (response.ok) {
      const body: BootstrapResponse = await response.json();
      setBootstrapData(body);
      setUserId(body.profile._id);
      setAuthStatus("AUTHENTICATED");
    } else {
      setAuthStatus("UNAUTHENTICATED");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const postLoginCallback = async () => {
    userListState.refetchUsers();
    proxyApplicationListState.refetchProxyApplications();
    await fetchProfile();
  }

  return (
    <div className="App" role="application">
      <CssBaseline />
      <ThemeProvider theme={theme}>
        {authStatus && (
          <Router
            postLoginCallback={postLoginCallback}
            authStatus={authStatus}
            profile={bootstrapData?.profile}
            account={bootstrapData?.account}
            plans={bootstrapData?.plans || []}
            proxyApplicationListState={proxyApplicationListState}
            userListState={userListState}
            regions={
              // Sort regions in reverse alphabetical order. This is a hack to make US appear first.
              bootstrapData?.regions.sort((a, b) => b.name.localeCompare(a.name)) || []
            }
          />
        )}
      </ThemeProvider>
      <ToastContainer />
    </div>
  );
}

export default App;
